// Pago a Proveedores (docs/108 B5): contratos y lecturas. Claves bajo ['emp', e, 'pagos', …].
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';

/** Sigla del documento de compra; el ingreso sin factura (2026-10-29) no tiene tipo SII: «ISF». */
const TD_COMPRA = { FACTURA: 'FCC', NOTA_CREDITO: 'NCC', NOTA_DEBITO: 'NDC' } as const;
export const siglaCompra = (d: Pick<DocumentoProveedor, 'clase' | 'tipoSii'>): string =>
  d.clase === 'FACTURA' && d.tipoSii === null ? 'ISF' : TD_COMPRA[d.clase];

export interface DocumentoProveedor {
  documentoId: string;
  clase: 'FACTURA' | 'NOTA_CREDITO' | 'NOTA_DEBITO';
  tipoSii: number | null;
  folio: string | null;
  fechaEmision: string;
  fechaVencimiento: string;
  montoTotal: string;
  proveedorEntidadId: string | null;
  proveedor: string;
  rutProveedor: string;
  notasCredito: string;
  neto: string;
  pagado: string;
  comprometido: string;
  saldo: string;
  disponible: string;
  /** Reclamado al proveedor esperando su NC: no se paga en dinero (sí con la NC). */
  retenido?: string;
  /** Lo que se puede pagar en dinero ahora (disponible menos lo retenido). */
  pagable?: string;
  reclamos?: Array<{
    origen: string;
    tipo: string;
    linea: number | null;
    descripcion: string | null;
    unidades: string | null;
    neto: string;
    motivo: string | null;
  }>;
  diasVencido: number;
}

export type EstadoPago =
  'PENDIENTE_AUTORIZACION' | 'AUTORIZADO' | 'RECHAZADO' | 'PAGADO' | 'ANULADO';
export const NOMBRE_ESTADO_PAGO: Record<EstadoPago, string> = {
  PENDIENTE_AUTORIZACION: 'Por autorizar',
  AUTORIZADO: 'Autorizado',
  RECHAZADO: 'Rechazado',
  PAGADO: 'Pagado',
  ANULADO: 'Anulado',
};

export interface PagoProveedor {
  pagoId: string;
  numero: number;
  proveedorEntidadId: string;
  loteId: string | null;
  estado: EstadoPago;
  total: string;
  aplicado: string;
  anticipo: string;
  medio: string;
  fechaProgramada: string;
  banco: string | null;
  numeroDocumento: string | null;
  observacion: string | null;
  solicitadoPor: string;
  solicitadoEn: string;
  autorizadoPor: string | null;
  autorizadoEn: string | null;
  motivoRechazo: string | null;
  pagadoEn: string | null;
  comprobante: string | null;
  motivoAnulacion: string | null;
  version: number;
  /** Cuenta de la empresa desde la que se paga y cuenta del proveedor a la que se transfiere. */
  cuentaOrigenId?: string | null;
  cuentaOrigen?: string | null;
  cuentaDestinoId?: string | null;
  cuentaDestino?: string | null;
  /** NC del proveedor tomadas como descuento en este pago. */
  notasCredito?: Array<{
    ncDocumentoId: string;
    ncFolio: string | null;
    documentoId: string;
    folio: string | null;
    monto: string;
  }>;
  aplicaciones: Array<{
    aplicacionId: string;
    tipo: 'APLICACION' | 'REVERSA';
    documentoId: string;
    tipoSii: number | null;
    folio: string | null;
    monto: string;
    aplicadoEn: string;
    reversada: boolean;
  }>;
}

export interface ResumenProveedor {
  proveedorEntidadId: string;
  proveedor: string;
  rut: string;
  documentos: number;
  saldo: string;
  vencido: string;
  comprometido: string;
  anticipos: string;
}

/** Nota de crédito del proveedor con lo que queda para tomarla como descuento. */
export interface NotaCreditoProveedor {
  documentoId: string;
  folio: string | null;
  fechaEmision: string;
  montoTotal: string;
  imputado: string;
  usado: string;
  comprometido: string;
  disponible: string;
}

/** Cuenta bancaria de la empresa (origen de transferencias y cheques). */
export interface CuentaEmpresa {
  id: string;
  banco: string;
  tipoCuenta: 'CORRIENTE' | 'VISTA' | 'AHORRO';
  numero: string;
  alias: string | null;
  activa: boolean;
  version: number;
  texto: string;
}

/** Cuenta bancaria de un cliente o proveedor (su ficha). */
export interface CuentaEntidad {
  id: string;
  entidadId: string;
  banco: string;
  tipoCuenta: 'CORRIENTE' | 'VISTA' | 'AHORRO' | 'RUT';
  numero: string;
  titularRut: string | null;
  titularNombre: string | null;
  correo: string | null;
  principal: boolean;
  activa: boolean;
  version: number;
  texto?: string;
}

export const NOMBRE_TIPO_CUENTA: Record<string, string> = {
  CORRIENTE: 'Cuenta corriente',
  VISTA: 'Cuenta vista',
  AHORRO: 'Cuenta de ahorro',
  RUT: 'CuentaRUT',
};

export const clavesPagos = {
  pagos: (e: string) => ['emp', e, 'pagos'] as const,
  documentos: (e: string, f: object) => ['emp', e, 'pagos', 'documentos', f] as const,
  lista: (e: string, f: object) => ['emp', e, 'pagos', 'lista', f] as const,
  resumen: (e: string) => ['emp', e, 'pagos', 'resumen'] as const,
  cartola: (e: string, p: string) => ['emp', e, 'pagos', 'cartola', p] as const,
  notasCredito: (e: string, p: string) => ['emp', e, 'pagos', 'nc', p] as const,
  destinos: (e: string, p: string) => ['emp', e, 'pagos', 'destinos', p] as const,
  cuentas: (e: string) => ['emp', e, 'pagos', 'cuentas'] as const,
};

export function useNotasCreditoCxp(empresaId: string, proveedorId: string | undefined) {
  return useQuery({
    queryKey: clavesPagos.notasCredito(empresaId, proveedorId ?? ''),
    queryFn: () =>
      api.get<NotaCreditoProveedor[]>('/tesoreria/cxp/notas-credito', {
        query: { empresaId, proveedorEntidadId: proveedorId ?? '' },
      }),
    enabled: !!proveedorId,
  });
}

export function useCuentasDestino(empresaId: string, proveedorId: string | undefined) {
  return useQuery({
    queryKey: clavesPagos.destinos(empresaId, proveedorId ?? ''),
    queryFn: () =>
      api.get<CuentaEntidad[]>('/tesoreria/cxp/cuentas-destino', {
        query: { empresaId, proveedorEntidadId: proveedorId ?? '' },
      }),
    enabled: !!proveedorId,
  });
}

export function useCuentasEmpresa(empresaId: string) {
  return useQuery({
    queryKey: clavesPagos.cuentas(empresaId),
    queryFn: () =>
      api.get<CuentaEmpresa[]>('/tesoreria/cuentas-bancarias', { query: { empresaId } }),
  });
}

export function useDocumentosCxp(
  empresaId: string,
  f: { proveedorEntidadId?: string | undefined; abiertos?: boolean },
  habilitado = true,
) {
  return useQuery({
    queryKey: clavesPagos.documentos(empresaId, f),
    queryFn: () =>
      api.get<DocumentoProveedor[]>('/tesoreria/cxp/documentos', {
        query: {
          empresaId,
          proveedorEntidadId: f.proveedorEntidadId,
          abiertos: f.abiertos ? 'true' : undefined,
        },
      }),
    enabled: habilitado,
  });
}

export function usePagos(
  empresaId: string,
  f: { proveedorEntidadId?: string | undefined; estado?: EstadoPago | undefined },
) {
  return useQuery({
    queryKey: clavesPagos.lista(empresaId, f),
    queryFn: () =>
      api.get<PagoProveedor[]>('/tesoreria/pagos', {
        query: { empresaId, proveedorEntidadId: f.proveedorEntidadId, estado: f.estado },
      }),
  });
}

export function useResumenCxp(empresaId: string) {
  return useQuery({
    queryKey: clavesPagos.resumen(empresaId),
    queryFn: () => api.get<ResumenProveedor[]>('/tesoreria/cxp/resumen', { query: { empresaId } }),
  });
}

export function useCartolaProveedor(
  empresaId: string,
  proveedorId: string | null,
  periodo: { desde?: string | undefined; hasta?: string | undefined } = {},
) {
  const desde = periodo.desde || undefined;
  const hasta = periodo.hasta || undefined;
  return useQuery({
    queryKey: [...clavesPagos.cartola(empresaId, proveedorId ?? ''), desde ?? '', hasta ?? ''],
    queryFn: () =>
      api.get<{
        proveedorEntidadId: string;
        saldoInicial: string;
        saldoFinal: string;
        movimientos: Array<{
          fecha: string;
          tipo: string;
          descripcion: string;
          documentoId: string | null;
          fechaVencimiento: string | null;
          cargo: string;
          abono: string;
          saldo: string;
        }>;
      }>('/tesoreria/cxp/cartola', {
        query: { empresaId, proveedorEntidadId: proveedorId ?? '', desde, hasta },
      }),
    enabled: !!proveedorId,
  });
}
